import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AIChatWidget } from "@/components/help/AIChatWidget";
import { 
  Search, 
  BookOpen, 
  Users, 
  FileText, 
  Video, 
  Download,
  HelpCircle,
  ShieldCheck,
  BarChart3,
  Folder,
  ArrowRight
} from "lucide-react";

const HelpCenter = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const quickStartGuides = [
    {
      title: "Getting Started",
      description: "Complete platform overview and first steps",
      icon: BookOpen,
      href: "/dashboard/help/getting-started",
      color: "text-primary"
    },
    {
      title: "Deal Management",
      description: "Learn the complete deal lifecycle",
      icon: FileText,
      href: "/dashboard/help/feature/deal-management",
      color: "text-blue-500"
    },
    {
      title: "Compliance Review",
      description: "Step-by-step compliance process",
      icon: ShieldCheck,
      href: "/dashboard/help/feature/compliance-review",
      color: "text-green-500"
    },
    {
      title: "Analytics & Reports",
      description: "Understanding dashboards and reports",
      icon: BarChart3,
      href: "/dashboard/help/feature/analytics",
      color: "text-purple-500"
    }
  ];

  const roleGuides = [
    {
      title: "Compliance Officer Guide",
      description: "Deal review, approval workflows, and compliance management",
      icon: ShieldCheck,
      href: "/dashboard/help/sop/compliance-officer"
    },
    {
      title: "Athletic Director Guide",
      description: "Department oversight, analytics, and revenue sharing",
      icon: Users,
      href: "/dashboard/help/sop/athletic-director"
    },
    {
      title: "Athlete Portal Guide",
      description: "Submitting deals, tracking earnings, and documentation",
      href: "/dashboard/help/sop/athlete",
      icon: Users
    }
  ];

  const downloadableSOPs = [
    {
      title: "Platform Overview SOP",
      description: "Complete platform documentation",
      pages: "45 pages"
    },
    {
      title: "Compliance Officer Workflow",
      description: "Detailed compliance procedures",
      pages: "28 pages"
    },
    {
      title: "Quick Reference Cards",
      description: "Common tasks and shortcuts",
      pages: "6 pages"
    }
  ];

  const faqs = [
    {
      question: "How do I submit a new NIL deal?",
      answer: "Athletes can submit deals through the Athlete Portal. Navigate to 'Submit Deal' and fill out the required information including brand details, compensation amount, and deliverables."
    },
    {
      question: "How long does deal approval take?",
      answer: "Most deals are reviewed within 2-3 business days. High-value deals (>$10,000) may require additional review time."
    },
    {
      question: "What documents do I need to upload?",
      answer: "Required documents include the signed contract, scope of work, and payment schedule. Additional documents may be requested during review."
    },
    {
      question: "How do I track my earnings?",
      answer: "Athletes can view all earnings through the Athlete Portal dashboard, which shows total earnings, pending compensation, and payment history."
    }
  ];

  return (
    <DashboardLayout>
      <AIChatWidget context={{ page: "help-center" }} />
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Help Center</h1>
          </div>
          <p className="text-muted-foreground mb-6">
            Find guides, tutorials, and answers to common questions
          </p>
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search help articles, guides, and FAQs..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <Tabs defaultValue="guides" className="space-y-8">
          <TabsList>
            <TabsTrigger value="guides">Quick Start Guides</TabsTrigger>
            <TabsTrigger value="roles">Role-Specific SOPs</TabsTrigger>
            <TabsTrigger value="downloads">Downloads</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
          </TabsList>

          <TabsContent value="guides" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {quickStartGuides.map((guide) => (
                <Card key={guide.title} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(guide.href)}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <guide.icon className={`h-8 w-8 ${guide.color}`} />
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <CardTitle className="mt-4">{guide.title}</CardTitle>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="roles" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {roleGuides.map((guide) => (
                <Card key={guide.title} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(guide.href)}>
                  <CardHeader>
                    <guide.icon className="h-8 w-8 text-primary mb-4" />
                    <CardTitle className="text-lg">{guide.title}</CardTitle>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      View Guide <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="downloads" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {downloadableSOPs.map((sop) => (
                <Card key={sop.title}>
                  <CardHeader>
                    <Download className="h-8 w-8 text-primary mb-4" />
                    <CardTitle className="text-lg">{sop.title}</CardTitle>
                    <CardDescription>{sop.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">{sop.pages}</p>
                    <Button variant="outline" className="w-full">
                      Download PDF
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="faqs" className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default HelpCenter;